/**
 * CLEARO before/after — scroll-linked cards (desktop only)
 *
 * As the section scrolls into view the two cards glide in from the sides and
 * meet in the centre; they rest there (perfectly still, so text stays sharp)
 * while the section is mid-screen, then glide back out as it scrolls away.
 * Works in both scroll directions.
 *
 * Writes --clr-ba-amount (0 = centred, 1 = peeking from the sides) plus the
 * travel distances --clr-ba-dx-before / --clr-ba-dx-after on the section.
 * Styles live in sections/clearo-before-after.liquid.
 */
(() => {
  if (window.clearoBeforeAfter) return;
  window.clearoBeforeAfter = true;

  const PEEK_PX = 110; // how much of each card shows at the screen edge before it slides in
  const REST_ZONE = 0.22; // share of the scroll range where the cards stay still in the centre
  const SMOOTHING = 0.18; // 0–1, higher = snappier follow

  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const smoothstep = (x) => x * x * (3 - 2 * x);

  class ScrollCards {
    constructor(section) {
      this.section = section;
      this.row = section.querySelector('.clr-ba__row');
      this.before = section.querySelector('.clr-ba__card--before');
      this.after = section.querySelector('.clr-ba__card--after');
      this.current = null;
      this.frame = null;
      this.onScroll = () => this.request();
      this.onResize = () => {
        this.measure();
        this.request();
      };
    }

    enable() {
      if (this.enabled) return;
      this.enabled = true;
      this.section.classList.add('clr-ba--fx');
      this.measure();
      this.current = this.target();
      this.apply(this.current);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    disable() {
      if (!this.enabled) return;
      this.enabled = false;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.section.classList.remove('clr-ba--fx');
      ['--clr-ba-amount', '--clr-ba-dx-before', '--clr-ba-dx-after'].forEach((v) =>
        this.section.style.removeProperty(v)
      );
    }

    // Travel distance for each card so that only PEEK_PX remains on screen
    measure() {
      this.section.style.setProperty('--clr-ba-amount', '0');
      const vw = document.documentElement.clientWidth;
      const b = this.before.getBoundingClientRect();
      const a = this.after.getBoundingClientRect();
      this.section.style.setProperty('--clr-ba-dx-before', `${Math.max(0, b.right - PEEK_PX)}px`);
      this.section.style.setProperty('--clr-ba-dx-after', `${Math.max(0, vw - a.left - PEEK_PX)}px`);
      if (this.current !== null) this.apply(this.current);
    }

    // 0 when the row is around the middle of the screen, rising to 1 at the edges
    target() {
      const r = this.row.getBoundingClientRect();
      const vh = window.innerHeight;
      const t = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
      const x = clamp((Math.abs(t) - REST_ZONE) / (1 - REST_ZONE), 0, 1);
      return smoothstep(x);
    }

    apply(value) {
      this.section.style.setProperty('--clr-ba-amount', value.toFixed(4));
    }

    request() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => this.tick());
    }

    tick() {
      this.frame = null;
      const goal = this.target();
      const diff = goal - this.current;
      this.current = Math.abs(diff) < 0.001 ? goal : this.current + diff * SMOOTHING;
      this.apply(this.current);
      if (this.current !== goal) this.request();
    }
  }

  const instances = new Map();

  const sync = () => {
    const on = desktop.matches && !reducedMotion.matches;
    instances.forEach((inst) => (on ? inst.enable() : inst.disable()));
  };

  const init = (root = document) => {
    root.querySelectorAll('.clr-ba').forEach((section) => {
      if (instances.has(section)) return;
      if (!section.querySelector('.clr-ba__card--before') || !section.querySelector('.clr-ba__card--after')) return;
      instances.set(section, new ScrollCards(section));
    });
    sync();
  };

  init();
  desktop.addEventListener('change', sync);
  reducedMotion.addEventListener('change', sync);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((inst, section) => {
      if (!event.target.contains(section)) return;
      inst.disable();
      instances.delete(section);
    });
  });
})();
